/*
 * Store change: the cart's Get a quote (snippets/quote-dialog.liquid). Ported from frankaggregate's cart-rfq.js,
 * second edition: validate the contact block, post the live cart plus the contact to the store's quote service,
 * then empty the cart and open the confirmation page (/pages/quote-received?ref=D12). A page, an email and an empty
 * cart, so nobody wonders whether it went through.
 *
 * The quote-only lock (any line without a price switches Check out off) is Liquid, in snippets/cart-summary.liquid.
 */
const PROVINCE_REQUIRED = new Set(['united states', 'canada']);

class QuoteForm extends HTMLElement {
  connectedCallback() {
    this.form = this.querySelector('form');
    if (!this.form) return;
    const { country, province } = this.form.elements;
    const preset = country.dataset.default;
    if (preset && [...country.options].some((o) => o.value === preset)) country.value = preset;
    else if (!country.value) country.value = 'United States';
    this.#syncProvinces(province.dataset.default);

    country.addEventListener('change', () => this.#syncProvinces());
    this.closest('dialog-component')?.addEventListener('dialog:open', () => this.#carryCartNote());
    this.form.addEventListener('focusin', () => {
      this.started ||= Date.now();
    });
    this.form.addEventListener('submit', (event) => {
      event.preventDefault();
      this.#submit();
    });
  }

  // The cart's Special instructions box (the cart note) would not reach the request on its own, so its text starts
  // the Notes field, where the buyer can see and edit it. A later change to the box replaces what was carried over,
  // never what the buyer typed here.
  #carryCartNote() {
    const note = document.getElementById('cart-note')?.value.trim();
    const notes = this.form.elements.notes;
    if (!note || (notes.value && notes.value !== this.carried)) return;
    notes.value = note;
    this.carried = note;
  }

  // Country <option>s carry data-provinces (JSON [[value, label], ...]). Provinces present: a select, required for
  // the United States and Canada. None: a free-text region field.
  #syncProvinces(preselect) {
    const { country, province, region } = this.form.elements;
    const label = this.querySelector('[data-province-label]');
    let provinces = [];
    try {
      provinces = JSON.parse(country.selectedOptions[0]?.dataset.provinces || '[]');
    } catch {
      provinces = [];
    }
    const required = PROVINCE_REQUIRED.has(country.value.toLowerCase());
    province.replaceChildren();
    if (provinces.length) {
      province.append(new Option(required ? 'Choose' : 'Choose (optional)', ''));
      for (const [value, name] of provinces) province.append(new Option(name || value, value));
      if (preselect) province.value = preselect;
      province.hidden = false;
      province.required = required;
      region.hidden = true;
      region.value = '';
      label.textContent = country.value === 'Canada' ? 'Province' : required ? 'State' : 'State / province';
    } else {
      province.hidden = true;
      province.required = false;
      region.hidden = false;
      label.textContent = 'Region (optional)';
    }
  }

  #contact() {
    const f = this.form.elements;
    const v = (name) => String(f[name]?.value || '').trim();
    return {
      name: v('name'),
      company: v('company'),
      email: v('email'),
      phone: v('phone'),
      country: v('country'),
      province: f.province.hidden ? v('region') : v('province'),
      machine: v('machine'),
      notes: v('notes'),
    };
  }

  #problem(c) {
    if (!c.name) return 'Please enter your name.';
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(c.email)) return 'Please enter a valid email address.';
    if (c.phone.replace(/\D/g, '').length < 7) return 'Please enter a phone number we can reach you on.';
    if (!c.country) return 'Please choose the ship-to country.';
    if (PROVINCE_REQUIRED.has(c.country.toLowerCase()) && !c.province) {
      return c.country === 'Canada' ? 'Please choose the ship-to province.' : 'Please choose the ship-to state.';
    }
    return null;
  }

  #say(message) {
    const el = this.querySelector('[data-quote-error]');
    el.textContent = message || '';
    el.hidden = !message;
  }

  async #submit() {
    const contact = this.#contact();
    const problem = this.#problem(contact);
    if (problem) return this.#say(problem);
    this.#say(null);

    const button = this.querySelector('[data-quote-submit]');
    const label = button.textContent;
    button.disabled = true;
    button.textContent = 'Sending…';
    const fail = (message) => {
      this.#say(message);
      button.disabled = false;
      button.textContent = label;
    };
    const support = this.dataset.supportEmail;
    const fallback = `We could not send your request. Please try again${support ? ` or email ${support}` : ''}.`;

    try {
      const cartUrl = this.dataset.cartUrl.replace(/\/$/, '');
      const cart = await fetch(`${cartUrl}.js`, { headers: { Accept: 'application/json' } }).then((r) => r.json());
      if (!cart?.items?.length) return fail('Your quote list is empty.');
      const items = cart.items.map((line) => ({
        variantId: line.variant_id ? `gid://shopify/ProductVariant/${line.variant_id}` : null,
        sku: line.sku || null,
        title: line.product_title || line.title || '',
        variantTitle: line.variant_title || null,
        quantity: line.quantity || 1,
      }));

      const response = await fetch(`${this.dataset.service.replace(/\/+$/, '')}/quote`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          items,
          contact,
          website: this.form.elements.website.value,
          t: this.started ? Date.now() - this.started : 0,
        }),
      });
      const body = await response.json().catch(() => ({}));
      if (!response.ok || !body.ok) return fail(body.error || fallback);

      await fetch(`${cartUrl}/clear.js`, { method: 'POST', headers: { Accept: 'application/json' } }).catch(() => {});
      const params = new URLSearchParams({ ref: String(body.reference || '').replace(/^#/, '') });
      if (body.duplicate) params.set('dup', '1');
      window.location.href = `${this.dataset.confirmUrl}?${params}`;
    } catch {
      fail(fallback);
    }
  }
}

if (!customElements.get('quote-form')) customElements.define('quote-form', QuoteForm);
